import { useSearchParams } from "react-router-dom";
import { Lock, ShieldCheck, ReceiptText } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { SITE } from "@/lib/site";

export default function PayBill() {
  const [params] = useSearchParams();
  const invoice = params.get("invoice") || "";
  const total = params.get("total") || "";
  const customer = params.get("customer") || "";
  const email = params.get("Email") || params.get("email") || "";

  return (
    <>
      <Seo
        title="Pay Your Invoice | CYAN Wireless"
        description="Securely pay your CYAN Wireless invoice online. Locally owned rural internet in Cortez, Dolores, Yellow Jacket & Lewis, Colorado."
        path="/pay"
      />
      <section className="relative pt-40 pb-24 sm:pt-48 bg-ink-950 overflow-hidden min-h-screen">
        <div className="absolute top-24 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-[140px]" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="overline mb-6 flex items-center gap-3"><span className="w-8 h-px bg-cyan-500" />Pay Invoice</p>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter text-white leading-[0.9]">
              Pay your<br /><span className="text-aurora">invoice.</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-400 max-w-md">
              Use this secure form to pay the invoice from your email. Once we receive it, we'll process your payment and send a confirmation.
            </p>

            {(invoice || total) && (
              <div className="mt-8 bg-ink-900 border border-cyan-500/30 rounded-2xl p-6" data-testid="invoice-summary">
                <div className="flex items-center gap-2 text-cyan-400 mb-3">
                  <ReceiptText size={16} /><span className="font-mono-tech text-xs uppercase tracking-widest">Invoice Summary</span>
                </div>
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <div className="font-mono-tech text-xs uppercase tracking-widest text-zinc-500">Invoice #</div>
                    <div className="font-display font-bold text-2xl text-white" data-testid="invoice-number">{invoice || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-tech text-xs uppercase tracking-widest text-zinc-500">Amount Due</div>
                    <div className="font-display font-black text-3xl text-cyan-400" data-testid="invoice-total">${total || "0.00"}</div>
                  </div>
                </div>
              </div>
            )}

            <ul className="mt-8 space-y-4">
              <li className="flex items-center gap-3 text-white"><span className="w-8 h-8 grid place-items-center rounded-full bg-cyan-500/15 text-cyan-400"><Lock size={16} /></span>Submitted directly to CYAN — never shared.</li>
              <li className="flex items-center gap-3 text-white"><span className="w-8 h-8 grid place-items-center rounded-full bg-cyan-500/15 text-cyan-400"><ShieldCheck size={16} /></span>We only keep the last 4 digits on file.</li>
            </ul>
            <p className="mt-6 text-sm text-zinc-500">Questions about your bill? Call us at {SITE.phone}.</p>
          </Reveal>

          <Reveal delay={0.1} className="bg-ink-900 border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-2 mb-2 text-cyan-400">
              <Lock size={16} /><span className="font-mono-tech text-xs uppercase tracking-widest">Secure Payment</span>
            </div>
            <h2 className="font-display font-bold text-2xl text-white mb-2">Payment details</h2>
            <p className="text-zinc-400 text-sm mb-6">Please confirm your details and enter your card information.</p>
            <LeadForm
              type="payment"
              submitLabel="Pay Invoice"
              showAddress
              showBilling
              initial={{ name: customer, email, invoice, total }}
              showMessage={false}
            />
          </Reveal>
        </div>
      </section>
    </>
  );
}
